import { useCallback, useEffect, useRef, useState } from "react";
import { OrgChart } from "d3-org-chart";
import * as d3 from "d3";
import ReactDOMServer from "react-dom/server";
import OHNSAOrgChartButton from "../views/OfficeHierarchyOrgChart/NSAOrgChart/OHNSAOrgChartButton";
import PDFOrgChartNode from "./pdfOrgChartNode";
import BulkFilter from "./BulkFilter";
import CustomLoader from "components/CustomLoader";
import axios from "axios";

export default function PDFView(Chart) {
  const [count, setCount] = useState(0);
  const setDownloading = Chart.downloading;
  const setShow = Chart.show;
  const pdf = Chart.pdf;
  const data = Chart.Chart;
  const title = Chart.title;
  const setCompactChart = Chart.compact;

  let list = useCallback(BulkFilter(data));
  let items = [];

  list.sections.forEach((value, key) => {
    items.push(key);
  });

  const containerStyles = {
    width: "60%",
    height: "100%",
  };

  const [root, setRoot] = useState(true);
  const d3JSContainer = useRef(null);
  const chartData = useRef(null);

  const api_base_url = process.env.REACT_APP_API_BASEURL;

  useEffect(() => {
    if (data && d3JSContainer.current) {
      chartData.current = new OrgChart();
      const chart = chartData.current;
      let children = 0;
      let rootDirectChildren = false;
      if (root) {
        rootDirectChildren = true;
      }

      chart
        .container(d3JSContainer.current)
        .data(root ? list.territory : list.sections[list.order[count - 1]])
        .linkUpdate(function () {
          d3.select(this).attr("stroke", "#ced4da").attr("stroke-width", 2);
        })
        .nodeWidth((d) => 300)
        .nodeHeight((d) => {
          const baseHeight = 120;
          // Set different heights for specific nodes
          if (d.data.districtType === "District") {
            return d.data.districtManager === null ? 167 : 204; // Height for District Manager
          } else if (d.data.type === "Office") {
            const height = d.data.associatedOffices.length * 10;
            return d.data.associatedOffices.length > 18
              ? 505
              : baseHeight + height;
          } else {
            return baseHeight;
          }
        })
        .childrenMargin((d) => 120)
        .compactMarginBetween((d) => 100)
        .compactMarginPair((d) => 90)
        .siblingsMargin((d) => 100)
        .buttonContent(({ node }) => {
          if (children < node.data._directSubordinates) {
            if (root && rootDirectChildren) {
              children =
                list.territory[list.territory.length - 1]._directSubordinates;
            } else {
              children = node.data._directSubordinates;
            }
          }
          return ReactDOMServer.renderToString(
            <OHNSAOrgChartButton
              children={node.children}
              directSubordinates={node.data._directSubordinates}
            />
          );
        })
        .nodeContent((node) => {
          return ReactDOMServer.renderToString(<PDFOrgChartNode data={node} />);
        })
        .compact(false)
        .layout("left");

      if (root) {
        chart.render();
        setRoot(false);
      } else {
        chart.expandAll().render();
      }
      download(children);
    }
  }, [count]);

  function download(children) {
    chartData.current.exportImg({
      save: false,
      full: true,
      scale: 10,

      onLoad: async (base64) => {
        let img = new Image();
        img.src = base64;
        img.onload = async function () {
          let { width, height } = img;
          if (count !== 0) {
            pdf.addPage();
          }

          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeight = pdf.internal.pageSize.getHeight();

          // Calculate scaling factors
          const scaleWidth = pdfWidth / width;
          const scaleHeight = pdfHeight / height;

          // Use the higher scaling factor to fit the image within the page
          const scale = Math.max(scaleWidth, scaleHeight);

          // Calculate new dimensions
          const newWidth = (width * scale) / 2;
          const newHeight = (width * scale) / 2;

          // Center the image on the page
          const x = (pdfWidth - newWidth) / 2;
          const y = (pdfHeight - newHeight) / 2;

          // Add the image to the PDF
          pdf.addImage(
            img,
            "PNG",
            x, // X-coordinate
            y, // Y-coordinate
            newWidth, // Scaled width
            newHeight, // Scaled height
            count,
            "FAST" // Use high-quality compression
          );

          if (count === items.length) {
            // Convert to base64 string
            const pdfBase64 = pdf.output("datauristring").split(",")[1];

            // Send to API
            const response = await axios.post(
              `${api_base_url}/OfficeHierarchy/OrgChartDownload`,
              {
                contentType: "application/pdf",
                base64: pdfBase64,
                fileName: title,
              },
              { responseType: "arraybuffer" }
            );

            // Trigger download
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = title;
            a.click();
            window.URL.revokeObjectURL(url);

            img.onerror = (e) => console.error("Image load error:", e);
            setDownloading(false);
            setCompactChart(false);
            setShow(false);
            window.location.reload();
          } else {
            setCount(count + 1);
          }
        };
      },
    });
  }

  return (
    <>
      <div>
        <CustomLoader />
        <div className="text-center">
          <h3>Downloading</h3>
          {count + 1} of {items.length + 1}
        </div>
      </div>
      <div
        style={{
          overflow: "hidden",
          height: "0px",
        }}
      >
        <div style={containerStyles} ref={d3JSContainer} className="relative" />
      </div>
    </>
  );
}
